import React, { useEffect, useState } from 'react';
import RequestFormLayout from 'components/layout/RequestFormLayout';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { notification } from 'antd';
import { useFormCleanup } from 'core/utils/useFormCleanup';
import { useSelector } from 'react-redux';
import { CombinedStates } from 'core/types';
import useRequestFlow from 'core/guestBooking/useRequestFlow';
import WorkTypeSelector from 'components/Form/WorkTypeSelector';
import WorkDetails from 'components/Form/WorkDetails';
import { CheckboxData } from 'components/Form/NestedCheckboxSearch/types';
import type { UploadFile } from 'antd/lib/upload/interface';
import { unwrapResult } from '@reduxjs/toolkit';
import { VisitRequestFormResponsePayload } from 'core/visitRequestForm/types';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';

const WorkTypeSelectionForm: React.FC = () => {
    const navigation = useNavigate();
    const { state: navState } = useLocation();
    const { stepData, visitRequest } = useSelector((state: CombinedStates) => state.visitRequestForm);
    const [workDetails, setWorkDetails] = useState<CheckboxData[]>([]);
    const [workReference, setWorkReference] = useState<string>('');
    const [descriptionOfWorks, setDescriptionOfWorks] = useState<string>('');
    const [comments, setComments] = useState<string>('');
    const { id } = useParams<{ id: string }>();
    const flow = useRequestFlow();
    const amending = navState?.amending;

    // Auto-cleanup request if user leaves without completing (never for a guest
    // booking — the draft is owned by the admin, not the guest).
    useFormCleanup({
        requestId: (!amending && !flow.guestMode) ? visitRequest.encryptedId : null,
        formBasePath: '/visitor-request/',
        deleteEndpoint: 'visit-request',
    });

    const handleBackClick = (navigationState: Record<string, unknown>) => {
        flow.goToStep(4, navigationState);
    };

    const getSelectedWorkTypes = () => {
        const selectedParent = workDetails.find((workType) => workType.checked);
        let selectedChild;
        if (selectedParent?.children) {
            selectedChild = selectedParent.children.find((child) => child.checked);
        }
        return [{ id: selectedChild?.id }, { id: selectedParent?.id, siteWorkTypeId: selectedParent?.siteWorkTypeId }];
    };

    const handleNextClick = (navigationState: Record<string, unknown>) => {
        const selectedWorkTypes = getSelectedWorkTypes();
        let error = { message: '', description: '' };

        // The cost code (entered back on the dates step) is off-limits as a work
        // reference, so it can be compared against here.
        const costCode = (visitRequest.costCode?.costCentre ?? stepData.costCode?.costCentre ?? '').trim();

        if (selectedWorkTypes.some(workType => workType.id === undefined)) {
            error = {
                message: 'Work Type Not Selected',
                description: 'Please select a parent and child work type to proceed',
            };
        } else if (descriptionOfWorks === '') {
            error = {
                message: 'Description of Works is Required',
                description: 'Please provide a brief description of the works you will be completing',
            };
        } else if (workReference === '') {
            error = {
                message: 'Work Reference is Required',
                description: 'Please provide a reference for the work you will be completing',
            };
        } else if (
            costCode !== ''
            && workReference.trim().toLowerCase() === costCode.toLowerCase()
        ) {
            error = {
                message: 'Invalid Work Reference',
                description: 'The work reference cannot be the same as the cost code. Please enter a different work reference.',
            };
        } else if (
            comments.trim() !== ''
            && comments.trim().toLowerCase() === descriptionOfWorks.trim().toLowerCase()
        ) {
            error = {
                message: 'Duplicate comment',
                description: 'The notes/comments are the same as the brief description of works. Please add different information, or leave the comments blank.',
            };
        }

        if (error.message !== '' && error.description !== '') {
            notification.error(error);
            return;
        }

        const collatedWorkDetails = {
            description: descriptionOfWorks,
            workReference,
            comments,
        };

        if (id !== undefined || flow.guestMode) {
            flow.updateStep(5, {
                workTypes: selectedWorkTypes,
                workDetails: collatedWorkDetails,
                amending,
                forcedStep: 5,
            }).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    flow.goToStep(6, navigationState);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const handleInitialLoad = () => {
        if (id !== undefined || flow.guestMode) {
            flow.loadStep(5, amending);
        } else {
            navigation('/dashboard', { replace: true });
            notification.error({
                message: 'Invalid Visit Request',
                description: 'Please select a visit request to proceed',
            });
        }
    };

    useEffect(() => {
        handleInitialLoad();
    }, []);

    useEffect(() => {
        if (descriptionOfWorks === '' && visitRequest.workDetails?.description) {
            setDescriptionOfWorks(visitRequest.workDetails?.description || '');
        }
        if (workReference === '' && visitRequest.workDetails?.workReference) {
            setWorkReference(visitRequest.workDetails?.workReference || '');
        }
        if (comments === '' && visitRequest.additionalComments) {
            setComments(visitRequest.additionalComments || '');
        }
    }, [visitRequest.workDetails]);

    useEffect(() => {
        if (stepData.workTypes) {
            const initialWorkTypes = stepData.workTypes.map(workType => ({
                ...workType,
                value: workType.id.toString(),
                checked: false,
                children: workType.children.map(siteWorkType => ({
                    ...siteWorkType,
                    value: siteWorkType.id.toString(),
                    checked: false,
                })),
            }));
            setWorkDetails(initialWorkTypes);

            if (visitRequest.workTypes) {
                const updatedWorkTypes = stepData.workTypes.map(workType => ({
                    ...workType,
                    value: workType.id.toString(),
                    checked: visitRequest.workTypes?.some((work) => work.workTypeId === workType.id) || false,
                    children: workType.children.map(siteWorkType => ({
                        ...siteWorkType,
                        value: siteWorkType.id.toString(),
                        checked: visitRequest.workTypes?.some((work) => work.workTypeId === siteWorkType.id) || false,
                    })),
                }));

                updatedWorkTypes.forEach((workType, index) => {
                    if (workType.children.length > 0) {
                        const childrenCount = workType.children.filter(child => child.checked).length;
                        if (childrenCount === workType.children.length) {
                            const updatedWorkType = { ...workType };
                            updatedWorkType.checked = true;
                            updatedWorkTypes[index] = updatedWorkType;
                        }
                    }
                });
                setWorkDetails(updatedWorkTypes);
            }
        }
    }, [stepData.workTypes]);

    const handleWorkTypeSelectorChange = (data: CheckboxData[]) => {
        setWorkDetails(data);
    };

    const handleAddAttachment = (file: UploadFile) => {
        if (id !== undefined || flow.guestMode) {
            flow.addAttachment(file);
        }
    };

    return (
        <RequestFormLayout
            currentStep={5}
            isNextEnabled
            isBackEnabled
            onBackClick={handleBackClick}
            onNextClick={handleNextClick}>
            <>
                {flow.guestMode ? (
                    <div
                        style={{ pointerEvents: 'none', opacity: 0.6 }}
                        aria-disabled="true">
                        <WorkTypeSelector workTypeData={workDetails} handleChange={handleWorkTypeSelectorChange} />
                    </div>
                ) : (
                    <WorkTypeSelector workTypeData={workDetails} handleChange={handleWorkTypeSelectorChange} />
                )}
                {workDetails.length > 0 ? (
                    <WorkDetails
                        descriptionOfWorks={descriptionOfWorks}
                        workReference={workReference}
                        comments={comments}
                        onDescriptionOfWorksChange={(value) => setDescriptionOfWorks(value)}
                        onWorkReferenceChange={(value) => setWorkReference(value)}
                        onFileUploadChange={handleAddAttachment}
                        onCommentsChange={(value) => setComments(value)} />
                ) : null}
            </>
        </RequestFormLayout>
    );
};

export default WorkTypeSelectionForm;
